"use client"

import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useId, useRef, useState } from "react"
import {
    FiCheck,
    FiGlobe,
    FiHelpCircle,
    FiMonitor,
    FiRotateCcw,
    FiSend,
    FiSmartphone,
    FiTerminal,
    FiX,
} from "react-icons/fi"
import { cn } from "@/lib/utils"

interface RecommendationModalProps {
    isOpen: boolean
    onClose: () => void
    initialQuery?: string
}

const PLATFORM_OPTIONS = [
    { id: "macOS", label: "macOS", icon: FiMonitor },
    { id: "Web", label: "Web", icon: FiGlobe },
    { id: "iOS", label: "iOS", icon: FiSmartphone },
    { id: "CLI", label: "CLI", icon: FiTerminal },
    { id: "Any", label: "Any", icon: FiHelpCircle },
] as const

export function RecommendationModal({
    isOpen,
    onClose,
    initialQuery = "",
}: RecommendationModalProps) {
    const titleId = useId()
    const descriptionId = useId()
    const workflowId = useId()
    const replacingToolId = useId()
    const contactId = useId()

    const [workflow, setWorkflow] = useState("")
    const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
    const [replacingTool, setReplacingTool] = useState("")
    const [contact, setContact] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const dialogRef = useRef<HTMLDivElement>(null)
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const successHeadingRef = useRef<HTMLHeadingElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Reset submission state & sync initialQuery when opened
    useEffect(() => {
        if (isOpen) {
            setIsSubmitted(false)
            setSubmitError(null)
            if (initialQuery) {
                setWorkflow(initialQuery)
            }
            const previouslyFocused = document.activeElement as HTMLElement
            const timer = setTimeout(() => {
                const supportsFinePointer = window.matchMedia(
                    "(hover: hover) and (pointer: fine)"
                ).matches
                const focusTarget = supportsFinePointer
                    ? textareaRef.current
                    : closeButtonRef.current
                focusTarget?.focus({ preventScroll: true })
            }, 100)
            return () => {
                clearTimeout(timer)
                previouslyFocused?.focus({ preventScroll: true })
            }
        }
    }, [isOpen, initialQuery])

    useEffect(() => {
        if (!isSubmitted) return
        successHeadingRef.current?.focus({ preventScroll: true })
    }, [isSubmitted])

    // Lock page scroll and keep keyboard navigation inside the dialog.
    useEffect(() => {
        if (!isOpen) return

        const scrollBarWidth =
            window.innerWidth - document.documentElement.clientWidth
        const originalOverflow = document.body.style.overflow
        const originalPaddingRight = document.body.style.paddingRight

        if (scrollBarWidth > 0) {
            document.body.style.paddingRight = `${scrollBarWidth}px`
        }
        document.body.style.overflow = "hidden"

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault()
                e.stopPropagation()
                onClose()
                return
            }

            if (e.key !== "Tab") return

            const focusableElements = Array.from(
                dialogRef.current?.querySelectorAll<HTMLElement>(
                    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ) ?? []
            ).filter((element) => element.offsetParent !== null)

            const firstElement = focusableElements[0]
            const lastElement = focusableElements.at(-1)
            if (!firstElement || !lastElement) return

            if (e.shiftKey && document.activeElement === firstElement) {
                e.preventDefault()
                lastElement.focus()
            } else if (!e.shiftKey && document.activeElement === lastElement) {
                e.preventDefault()
                firstElement.focus()
            }
        }

        document.addEventListener("keydown", handleKeyDown, true)
        return () => {
            document.body.style.overflow = originalOverflow
            document.body.style.paddingRight = originalPaddingRight
            document.removeEventListener("keydown", handleKeyDown, true)
        }
    }, [isOpen, onClose])

    const togglePlatform = (id: string) => {
        if (id === "Any") {
            setSelectedPlatforms((prev) =>
                prev.includes("Any") ? [] : ["Any"]
            )
            return
        }

        setSelectedPlatforms((prev) => {
            const filtered = prev.filter((p) => p !== "Any")
            if (filtered.includes(id)) {
                return filtered.filter((p) => p !== id)
            }
            return [...filtered, id]
        })
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (workflow.trim().length < 3) return

        setIsSubmitting(true)
        setSubmitError(null)

        try {
            const formData = new FormData(e.currentTarget)
            const response = await fetch("/api/recommendations", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    workflow,
                    platforms: selectedPlatforms,
                    replacingTool,
                    contact,
                    website: formData.get("website"),
                }),
            })
            const result = (await response.json()) as { error?: string }

            if (!response.ok) {
                throw new Error(
                    result.error ?? "Unable to submit your request right now."
                )
            }

            setIsSubmitted(true)
        } catch (error) {
            setSubmitError(
                error instanceof Error
                    ? error.message
                    : "Unable to submit your request right now."
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleReset = () => {
        setWorkflow("")
        setSelectedPlatforms([])
        setReplacingTool("")
        setContact("")
        setIsSubmitted(false)
        setSubmitError(null)
        setTimeout(() => {
            textareaRef.current?.focus()
        }, 50)
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                        aria-hidden="true"
                    />

                    {/* Modal Window */}
                    <motion.div
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        aria-describedby={descriptionId}
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 10 }}
                        transition={{
                            duration: 0.22,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="relative z-10 flex max-h-[90dvh] w-full max-w-xl flex-col overflow-hidden border border-border/70 bg-background shadow-2xl dark:border-border/40"
                        data-lenis-prevent="true"
                    >
                        {/* Modal Header */}
                        <div className="flex items-start justify-between border-border/70 border-b p-5 sm:p-6 dark:border-border/40">
                            <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="size-2 bg-blue-500" />
                                    <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                                        Recommendation Desk
                                    </span>
                                </div>
                                <h2
                                    id={titleId}
                                    className="font-medium text-foreground text-xl tracking-tight sm:text-2xl"
                                >
                                    Ask for a Tool
                                </h2>
                                <p
                                    id={descriptionId}
                                    className="font-light text-muted-foreground text-xs leading-relaxed sm:text-sm"
                                >
                                    Need software for a specific workflow or
                                    pain point? Describe what you want to
                                    accomplish and I&apos;ll test or recommend
                                    the best app.
                                </p>
                            </div>

                            {/* Close Button with ESC hint */}
                            <div className="flex items-center gap-2 pl-3">
                                <span className="hidden border border-border/60 bg-muted/30 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-block">
                                    ESC
                                </span>
                                <button
                                    ref={closeButtonRef}
                                    type="button"
                                    onClick={onClose}
                                    className="border border-border/70 p-1.5 text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-muted hover:text-foreground dark:border-border/40"
                                    aria-label="Close dialog"
                                >
                                    <FiX className="size-4" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body: Form or Success */}
                        <div
                            className="overflow-y-auto overscroll-contain p-5 sm:p-6"
                            data-lenis-prevent="true"
                        >
                            {!isSubmitted ? (
                                <form
                                    onSubmit={handleSubmit}
                                    aria-busy={isSubmitting}
                                    className="flex flex-col gap-5"
                                >
                                    <input
                                        type="text"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        aria-hidden="true"
                                        className="absolute -left-[9999px]"
                                    />
                                    {/* 01: Workflow description */}
                                    <div className="flex flex-col gap-2">
                                        <label
                                            htmlFor={workflowId}
                                            className="font-mono text-[11px] text-foreground uppercase tracking-wider"
                                        >
                                            01 / What workflow or task are you
                                            trying to do?{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <textarea
                                            ref={textareaRef}
                                            id={workflowId}
                                            rows={3}
                                            maxLength={2000}
                                            value={workflow}
                                            onChange={(e) =>
                                                setWorkflow(e.target.value)
                                            }
                                            placeholder="e.g. A fast Mac menu bar app to compress images without cloud uploads, or a clean CLI tool to manage local Docker containers without Electron..."
                                            required
                                            className="w-full resize-none border border-border/70 bg-card/30 p-3 font-normal text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/50 sm:text-sm dark:border-border/40 dark:bg-card/15"
                                        />
                                    </div>

                                    {/* 02: Target Platform */}
                                    <fieldset className="m-0 flex flex-col border-0 p-0">
                                        <legend className="mb-2.5 font-mono text-[11px] text-foreground uppercase tracking-wider">
                                            02 / Target Platform(s)
                                        </legend>
                                        <div className="flex flex-wrap gap-1.5">
                                            {PLATFORM_OPTIONS.map((item) => {
                                                const Icon = item.icon
                                                const isSelected =
                                                    selectedPlatforms.includes(
                                                        item.id
                                                    )
                                                return (
                                                    <button
                                                        key={item.id}
                                                        type="button"
                                                        aria-pressed={
                                                            isSelected
                                                        }
                                                        onClick={() =>
                                                            togglePlatform(
                                                                item.id
                                                            )
                                                        }
                                                        className={cn(
                                                            "inline-flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-xs transition-all",
                                                            isSelected
                                                                ? "border-foreground bg-foreground font-medium text-background"
                                                                : "border-border/70 bg-background text-muted-foreground hover:border-foreground/40 hover:bg-muted hover:text-foreground dark:border-border/40"
                                                        )}
                                                    >
                                                        <Icon className="size-3.5" />
                                                        <span>
                                                            {item.label}
                                                        </span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </fieldset>

                                    {/* 03: Current tool being replaced */}
                                    <div className="flex flex-col gap-2">
                                        <label
                                            htmlFor={replacingToolId}
                                            className="font-mono text-[11px] text-foreground uppercase tracking-wider"
                                        >
                                            03 / Current tool being replaced
                                        </label>
                                        <input
                                            type="text"
                                            id={replacingToolId}
                                            value={replacingTool}
                                            maxLength={200}
                                            onChange={(e) =>
                                                setReplacingTool(e.target.value)
                                            }
                                            placeholder="e.g. Postman, Notion, Docker Desktop..."
                                            className="w-full border border-border/70 bg-card/30 px-3 py-2 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/50 sm:text-sm dark:border-border/40 dark:bg-card/15"
                                        />
                                    </div>

                                    {/* 04: Contact / Handle */}
                                    <div className="flex flex-col gap-2">
                                        <label
                                            htmlFor={contactId}
                                            className="font-mono text-[11px] text-foreground uppercase tracking-wider"
                                        >
                                            04 / Where should I reply?
                                        </label>
                                        <input
                                            type="text"
                                            id={contactId}
                                            value={contact}
                                            maxLength={200}
                                            onChange={(e) =>
                                                setContact(e.target.value)
                                            }
                                            placeholder="Email or @handle (X, GitHub, Telegram)"
                                            className="w-full border border-border/70 bg-card/30 px-3 py-2 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/50 sm:text-sm dark:border-border/40 dark:bg-card/15"
                                        />
                                        <p className="text-muted-foreground text-xs">
                                            Leave empty if you just want it
                                            considered for addition to this
                                            curated list.
                                        </p>
                                    </div>

                                    {submitError && (
                                        <p
                                            role="alert"
                                            className="border border-red-500/30 bg-red-500/5 px-3 py-2 text-red-600 text-xs leading-relaxed dark:text-red-400"
                                        >
                                            {submitError} You can also email me
                                            directly at{" "}
                                            <a
                                                href="mailto:phalla.doll@gmail.com"
                                                className="font-medium underline underline-offset-2"
                                            >
                                                phalla.doll@gmail.com
                                            </a>
                                            .
                                        </p>
                                    )}

                                    {/* Action Bar */}
                                    <div className="mt-2 flex items-center justify-end gap-2 border-border/70 border-t pt-4 dark:border-border/40">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="border border-border/70 bg-background px-4 py-2 font-mono text-muted-foreground text-xs uppercase tracking-wider transition-colors hover:bg-muted hover:text-foreground dark:border-border/40"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={
                                                workflow.trim().length < 3 ||
                                                isSubmitting
                                            }
                                            className="inline-flex items-center justify-center gap-2 border border-foreground bg-foreground px-5 py-2 font-medium font-mono text-background text-xs uppercase tracking-wider transition-all hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            <FiSend className="size-3.5" />
                                            <span>
                                                {isSubmitting
                                                    ? "Submitting..."
                                                    : "Submit Request"}
                                            </span>
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                /* Success View */
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex flex-col items-center py-4 text-center"
                                >
                                    <div className="mb-4 flex size-12 items-center justify-center border border-green-500/40 bg-green-500/10 text-green-600 dark:text-green-400">
                                        <FiCheck className="size-6" />
                                    </div>
                                    <span className="font-mono text-[10px] text-green-600 uppercase tracking-widest dark:text-green-400">
                                        Request Logged
                                    </span>
                                    <h3
                                        ref={successHeadingRef}
                                        tabIndex={-1}
                                        className="mt-1 font-medium text-2xl text-foreground tracking-tight outline-none"
                                    >
                                        Thanks for the request!
                                    </h3>
                                    <p className="mt-2 max-w-md font-light text-muted-foreground text-sm leading-relaxed">
                                        I review tool requests to discover,
                                        benchmark, and find native or
                                        lightweight alternatives. If it makes
                                        the cut, it will appear right here in
                                        the Everyday Stack!
                                    </p>

                                    {/* Submitted Request Summary Box */}
                                    <div className="mt-6 w-full border border-border/70 bg-muted/20 p-4 text-left font-mono text-xs dark:border-border/40 dark:bg-muted/10">
                                        <div className="flex items-center justify-between border-border/70 border-b pb-2 text-[10px] text-muted-foreground uppercase dark:border-border/40">
                                            <span>Summary of Request</span>
                                            <span className="text-green-600 dark:text-green-400">
                                                Status: Pending Review
                                            </span>
                                        </div>
                                        <div className="mt-3 flex flex-col gap-2">
                                            <div>
                                                <span className="text-muted-foreground">
                                                    Workflow:{" "}
                                                </span>
                                                <span className="break-words text-foreground">
                                                    &ldquo;{workflow}&rdquo;
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">
                                                    Platforms:{" "}
                                                </span>
                                                <span className="break-words text-foreground">
                                                    {selectedPlatforms.length >
                                                    0
                                                        ? selectedPlatforms.join(
                                                              ", "
                                                          )
                                                        : "Any / Unspecified"}
                                                </span>
                                            </div>
                                            {replacingTool && (
                                                <div>
                                                    <span className="text-muted-foreground">
                                                        Replacing:{" "}
                                                    </span>
                                                    <span className="break-words text-foreground">
                                                        {replacingTool}
                                                    </span>
                                                </div>
                                            )}
                                            {contact && (
                                                <div>
                                                    <span className="text-muted-foreground">
                                                        Contact:{" "}
                                                    </span>
                                                    <span className="break-words text-foreground">
                                                        {contact}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Success Actions */}
                                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleReset}
                                            className="inline-flex items-center gap-1.5 border border-border/70 bg-background px-4 py-2 font-mono text-muted-foreground text-xs uppercase tracking-wider transition-colors hover:bg-muted hover:text-foreground dark:border-border/40"
                                        >
                                            <FiRotateCcw className="size-3.5" />
                                            <span>Ask for another tool</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="border border-foreground bg-foreground px-5 py-2 font-mono text-background text-xs uppercase tracking-wider transition-colors hover:bg-foreground/90"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}
